import React, { useState, useEffect, useCallback, useRef } from "react";
import { DialogLayout, Button, TextField } from "../../../../ui";
import { FeatherSearch, FeatherCheck } from "@subframe/core";
import { GetBillsForVendorUseCase } from "../../../../shared/domain/use-cases/GetBillsForVendorUseCase";
import { BillRepositoryImpl } from "../../../../shared/data/repositories/BillRepositoryImpl";
import { BillListItemEntity } from "../../../../shared/domain/entities/BillListItemEntity";
import { VendorBill } from "../../../../types";
import {
  useToast,
  ErrorBanner,
} from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";

interface ApplyVendorCreditsModalProps {
  open: boolean;
  vendorId: string;
  vendorName: string;
  availableCredits: number;
  currencySymbol: string;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const ApplyVendorCreditsModal: React.FC<ApplyVendorCreditsModalProps> = ({
  open,
  vendorId,
  vendorName,
  availableCredits,
  currencySymbol,
  onOpenChange,
  onSuccess,
}) => {
  const { showSuccess, showError, showWarning } = useToast();
  const {
    errors,
    setApiErrors,
    setFieldError,
    clearErrors,
    hasErrors: checkHasErrors,
  } = useErrorHandling();

  // State for bills
  const [loadingBills, setLoadingBills] = useState(false);
  const [billsOffset, setBillsOffset] = useState(0);
  const [billsHasMore, setBillsHasMore] = useState(false);
  const [billsLoading, setBillsLoading] = useState(false);
  const [billSearchTerm, setBillSearchTerm] = useState<string>("");

  // Initialize use case
  const getBillsUseCase = React.useMemo(
    () => new GetBillsForVendorUseCase(new BillRepositoryImpl()),
    []
  );

  // State for selected bills with amounts
  const [selectedBills, setSelectedBills] = useState<
    Array<{
      bill: VendorBill;
      amount: number;
      isSelected: boolean;
    }>
  >([]);

  // State for applying credits
  const [isApplying, setIsApplying] = useState(false);

  // Track if pay in full has been used
  const [payInFullUsed, setPayInFullUsed] = useState(false);

  // Search timeout ref for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevSearchTermRef = useRef<string>("");

  // Helper function to convert BillListItemEntity to VendorBill
  const convertToVendorBill = (entity: BillListItemEntity): VendorBill => {
    return {
      bill_id: entity.bill_id,
      bill_number: entity.bill_number,
      vendor_id: entity.vendor_id,
      vendor_name: entity.vendor_name,
      total: entity.total,
      balance: entity.balance,
      tds_total: entity.tds_total,
      currency_code: entity.currency_code,
      currency_symbol: entity.currency_symbol,
    };
  };

  // Fetch vendor bills
  const fetchVendorBills = useCallback(
    async (reset: boolean = true) => {
      if (reset) {
        setBillsOffset(0);
      }

      setLoadingBills(true);
      setBillsLoading(true);
      try {
        const result = await getBillsUseCase.execute(
          vendorId,
          5,
          reset ? 0 : billsOffset,
          billSearchTerm || undefined
        );

        // Filter bills with balance > 0 and convert to VendorBill
        const billsWithBalance = result.items
          .filter((bill) => bill.balance > 0)
          .map(convertToVendorBill);

        setBillsHasMore(result.pagination.has_more);
        setBillsOffset(result.pagination.offset + result.pagination.limit);

        // Initialize or update selectedBills
        const newSelectedBills = billsWithBalance.map((bill) => ({
          bill,
          amount: 0,
          isSelected: false,
        }));

        if (reset) {
          setSelectedBills(newSelectedBills);
        } else {
          setSelectedBills((prev) => [...prev, ...newSelectedBills]);
        }
      } catch (error) {
        console.error("Failed to fetch vendor bills:", error);
        if (reset) {
          setSelectedBills([]);
        }
      } finally {
        setLoadingBills(false);
        setBillsLoading(false);
      }
    },
    [getBillsUseCase, billsOffset, billSearchTerm, vendorId]
  );

  const loadMoreBills = useCallback(() => {
    if (!billsHasMore || billsLoading) return;
    fetchVendorBills(false);
  }, [billsHasMore, billsLoading, fetchVendorBills]);

  // Fetch bills when modal opens
  useEffect(() => {
    if (open) {
      fetchVendorBills(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (!open) return;

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const currentSearchTerm = billSearchTerm.trim();
    const previousSearchTerm = prevSearchTermRef.current.trim();

    // If search term is empty and it was previously non-empty (user cleared the search), refetch immediately
    if (!currentSearchTerm && previousSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      setBillsOffset(0);
      fetchVendorBills(true);
      return;
    }

    // If search term has value, debounce the search
    if (currentSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      searchTimeoutRef.current = setTimeout(() => {
        setBillsOffset(0);
        fetchVendorBills(true);
      }, 300);
    } else {
      // Update ref even when search is empty (for initial state)
      prevSearchTermRef.current = currentSearchTerm;
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billSearchTerm, open]);

  // Calculate remaining credits
  const calculateRemainingCredits = () => {
    const totalUsed = selectedBills
      .filter((item) => item.isSelected)
      .reduce((sum, item) => sum + (item.amount || 0), 0);
    return Math.max(0, availableCredits - totalUsed);
  };

  const remainingCredits = calculateRemainingCredits();

  // Get maximum amount that can be entered for a specific bill
  const getMaxAmountForBill = (billId: string): number => {
    const currentBill = selectedBills.find(
      (item) => item.bill.bill_id === billId
    );

    if (!currentBill) return 0;

    // Calculate total credits used by other selected bills
    const otherBillsTotal = selectedBills
      .filter((item) => item.isSelected && item.bill.bill_id !== billId)
      .reduce((sum, item) => sum + (item.amount || 0), 0);

    const availableCreditsForThis = availableCredits - otherBillsTotal;

    // Get bill's remaining balance
    const billBalance = currentBill.bill.balance || 0;

    // Return min of available credits and bill balance
    return Math.max(0, Math.min(availableCreditsForThis, billBalance));
  };

  // Format amount helper
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleClose = () => {
    setSelectedBills([]);
    setBillSearchTerm("");
    setPayInFullUsed(false);
    setBillsOffset(0);
    clearErrors();
    onOpenChange(false);
  };

  const handleBillSelect = (billId: string) => {
    setSelectedBills((prev) => {
      const updated = prev.map((item) => {
        if (item.bill.bill_id === billId) {
          const newIsSelected = !item.isSelected;
          return {
            ...item,
            isSelected: newIsSelected,
            amount: newIsSelected ? item.amount : 0,
          };
        }
        return item;
      });

      const hasNewlySelectedWithoutAmount = updated.some(
        (item) => item.isSelected && (item.amount === 0 || !item.amount)
      );

      if (hasNewlySelectedWithoutAmount) {
        setPayInFullUsed(false);
      }

      return updated;
    });
  };

  const handleAmountChange = (billId: string, amount: number) => {
    setSelectedBills((prev) => {
      const billItem = prev.find((item) => item.bill.bill_id === billId);

      if (!billItem) return prev;

      const roundedAmount = Math.round(amount * 100) / 100;
      const maxAllowed = getMaxAmountForBill(billId);

      if (roundedAmount > maxAllowed + 0.001) {
        showWarning(
          `Amount cannot exceed ${currencySymbol}${formatAmount(maxAllowed)}`
        );
        return prev;
      }

      // Check if amount exceeds bill balance
      const billBalance = billItem.bill.balance || 0;
      if (roundedAmount > billBalance + 0.001) {
        showWarning(
          `Amount cannot exceed the bill's remaining balance of ${currencySymbol}${formatAmount(
            billBalance
          )}.`
        );
        return prev;
      }

      // Check if amount exceeds available credits
      const otherBillsTotal = prev
        .filter((item) => item.isSelected && item.bill.bill_id !== billId)
        .reduce((sum, item) => sum + (item.amount || 0), 0);

      if (otherBillsTotal + roundedAmount > availableCredits + 0.001) {
        showWarning(
          `Total amount exceeds available credits. Available: ${currencySymbol}${formatAmount(
            availableCredits - otherBillsTotal
          )}`
        );
        return prev;
      }

      const updated = prev.map((item) =>
        item.bill.bill_id === billId ? { ...item, amount: roundedAmount } : item
      );

      setPayInFullUsed(false);
      return updated;
    });
  };

  const handlePayInFull = () => {
    clearErrors();
    const selectedItemsWithoutAmounts = selectedBills.filter(
      (item) => item.isSelected && (item.amount === 0 || !item.amount)
    );

    if (selectedItemsWithoutAmounts.length === 0) {
      showWarning(
        "Please select at least one bill without an amount to pay in full."
      );
      return;
    }

    // Calculate remaining credits
    const alreadyUsedCredits = selectedBills
      .filter((item) => item.isSelected && item.amount > 0)
      .reduce((sum, item) => sum + (item.amount || 0), 0);

    let remainingCreditsForPayInFull = availableCredits - alreadyUsedCredits;
    const billsToUpdate: Array<{
      billId: string;
      amount: number;
    }> = [];

    for (const item of selectedItemsWithoutAmounts) {
      if (remainingCreditsForPayInFull <= 0) break;

      const billBalance = item.bill.balance || 0;

      // Payment amount is min of: available credits, bill balance
      let paymentAmount = Math.min(
        Math.max(0, remainingCreditsForPayInFull),
        billBalance
      );
      paymentAmount = Math.round(paymentAmount * 100) / 100;

      if (paymentAmount > 0) {
        billsToUpdate.push({
          billId: item.bill.bill_id,
          amount: paymentAmount,
        });
        remainingCreditsForPayInFull -= paymentAmount;
        remainingCreditsForPayInFull =
          Math.round(remainingCreditsForPayInFull * 100) / 100;
      }
    }

    setSelectedBills((prev) => {
      const updated = prev.map((item) => {
        const updateInfo = billsToUpdate.find(
          (u) => u.billId === item.bill.bill_id
        );
        if (updateInfo) {
          return { ...item, amount: updateInfo.amount };
        }
        return item;
      });
      return updated;
    });

    if (billsToUpdate.length > 0) {
      showSuccess("Applied payment amounts to selected bills.");
    }

    setPayInFullUsed(true);
  };

  const handleApply = async () => {
    setIsApplying(true);
    clearErrors();

    try {
      const selectedBillsWithAmounts = selectedBills.filter(
        (item) => item.isSelected && item.amount > 0
      );
      const selectedBillsWithoutAmounts = selectedBills.filter(
        (item) => item.isSelected && item.amount <= 0
      );

      if (selectedBillsWithoutAmounts.length > 0) {
        showWarning(
          "Please enter amounts for all selected bills before applying credits."
        );
        setFieldError("bills", "Please enter amounts for all selected bills");
        setIsApplying(false);
        return;
      }

      if (selectedBillsWithAmounts.length === 0) {
        showWarning("Please select at least one bill with an amount.");
        setFieldError("bills", "Please select at least one bill");
        setIsApplying(false);
        return;
      }

      // Import the API function
      const { applyCreditsToBills } = await import(
        "../../data/datasources/RecoApiDataSource"
      );

      const bills = selectedBillsWithAmounts.map((item) => ({
        bill_id: item.bill.bill_id,
        bill_number: item.bill.bill_number || item.bill.bill_id,
        amount: item.amount,
        balance: item.bill.balance,
      }));

      const response = await applyCreditsToBills(vendorId, bills);

      if (response.success) {
        showSuccess("Credits applied to bills successfully!");
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        showError(
          `Failed to apply credits: ${response.message || "Unknown error"}`
        );
        setFieldError("api", response.message || "Failed to apply credits");
      }
    } catch (error: any) {
      console.error("Error applying credits:", error);
      const errorMessage =
        error?.message || "Failed to apply credits. Please try again.";
      showError(errorMessage);
      setApiErrors(error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasValidationErrors = () => {
    const selectedBillsWithoutAmounts = selectedBills.filter(
      (item) => item.isSelected && item.amount <= 0
    );
    return selectedBillsWithoutAmounts.length > 0;
  };

  return (
    <DialogLayout open={open} onOpenChange={handleClose}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Apply Credits to Bills
        </span>
        {checkHasErrors() && (
          <ErrorBanner
            errors={errors}
            title="Please fix the following errors:"
          />
        )}
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {vendorName}
            </span>
            <span className="text-heading-3 font-heading-3 text-success-600">
              {currencySymbol}
              {formatAmount(availableCredits)} Available
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Select Bills
            </span>
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search bills"
                  value={billSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setBillSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex w-full items-center justify-between">
                <span className="text-body-bold font-body-bold text-default-font">
                  Available Bills
                  {loadingBills && (
                    <span className="text-body font-body text-neutral-500 ml-2">
                      (Loading...)
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-caption font-caption text-neutral-400">
                      Remaining credits:
                    </span>
                    <span
                      className={`text-caption font-caption ${
                        remainingCredits >= 0
                          ? "text-success-600"
                          : "text-error-600"
                      }`}
                    >
                      {currencySymbol}
                      {formatAmount(Math.max(0, remainingCredits))}
                    </span>
                  </div>
                  {selectedBills.some((item) => item.isSelected) && (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={handlePayInFull}
                      disabled={payInFullUsed}
                    >
                      Pay in Full
                    </Button>
                  )}
                </div>
              </div>
              {selectedBills.length > 0 ? (
                selectedBills.map((item) => (
                  <div
                    key={item.bill.bill_id}
                    className={`flex w-full items-center justify-between rounded-md border border-solid px-4 py-3 ${
                      item.isSelected
                        ? item.amount > 0
                          ? "border-success-600 bg-success-100"
                          : "border-warning-600 bg-warning-50"
                        : "border-neutral-border bg-default-background"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <Button
                        className={`h-6 w-6 flex-none ${
                          item.isSelected
                            ? "bg-success-600 text-white"
                            : "border-2 border-dashed border-neutral-300"
                        }`}
                        variant={
                          item.isSelected
                            ? "brand-primary"
                            : "neutral-secondary"
                        }
                        size="small"
                        icon={item.isSelected ? <FeatherCheck /> : undefined}
                        onClick={() => handleBillSelect(item.bill.bill_id)}
                      />
                      <div className="flex flex-col items-start">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {item.bill.bill_number || item.bill.bill_id}
                        </span>
                        <span className="text-body font-body text-neutral-500">
                          {item.bill.vendor_name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-12">
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Full
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.bill.currency_symbol || item.bill.currency_code}
                          {formatAmount(item.bill.total)}
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Paid
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.bill.currency_symbol || item.bill.currency_code}
                          {formatAmount(item.bill.total - item.bill.balance)}
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Remaining
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.bill.currency_symbol || item.bill.currency_code}
                          {formatAmount(item.bill.balance)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <TextField
                          className="h-auto w-32 flex-none"
                          label=""
                          helpText=""
                        >
                          <TextField.Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={`Max: ${
                              item.bill.currency_symbol ||
                              item.bill.currency_code
                            }${formatAmount(
                              getMaxAmountForBill(item.bill.bill_id)
                            )}`}
                            value={
                              item.isSelected
                                ? item.amount > 0
                                  ? item.amount.toString()
                                  : ""
                                : ""
                            }
                            disabled={!item.isSelected}
                            onWheel={(e) => {
                              e.currentTarget.blur();
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const inputValue = event.target.value;
                              if (inputValue === "" || inputValue === ".") {
                                handleAmountChange(item.bill.bill_id, 0);
                                return;
                              }

                              const decimalRegex = /^\d+(\.\d{0,2})?$/;
                              if (!decimalRegex.test(inputValue)) {
                                return;
                              }

                              const value = parseFloat(inputValue) || 0;
                              const roundedValue =
                                Math.round(value * 100) / 100;
                              handleAmountChange(
                                item.bill.bill_id,
                                roundedValue
                              );
                            }}
                          />
                        </TextField>
                      </div>
                      <Button
                        className={`h-8 w-20 flex-none ${
                          item.isSelected ? "bg-success-600 text-white" : ""
                        }`}
                        size="small"
                        onClick={() => handleBillSelect(item.bill.bill_id)}
                      >
                        {item.isSelected ? "Selected" : "Select"}
                      </Button>
                    </div>
                  </div>
                ))
              ) : !loadingBills ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                  <span className="text-body font-body text-neutral-500">
                    No bills found for {vendorName}
                  </span>
                </div>
              ) : null}
              {billsHasMore && (
                <div className="flex w-full items-center justify-center pt-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={loadMoreBills}
                    disabled={billsLoading}
                    loading={billsLoading}
                  >
                    {billsLoading ? "Loading..." : "Load More Bills"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-6">
          <div className="flex items-center gap-2">
            <Button variant="neutral-tertiary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={hasValidationErrors() || isApplying}
              loading={isApplying}
              className={
                hasValidationErrors() ? "opacity-50 cursor-not-allowed" : ""
              }
            >
              Apply Credits
            </Button>
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ApplyVendorCreditsModal;
